import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { RtAnalyticsModule, TrackDirective } from '@recruitee/analytics';
import { FollowButtonComponent } from '@recruitee/candidate-ui';
import { TranslatePipe } from '@recruitee/i18n';
import { AssignedLocationsInfoContainerComponent } from '@recruitee/locations';
import { OfferAnalytics } from '@recruitee/offers';
import { OfferKind, OfferStatus } from '@recruitee/offers-types';
import type { OfferFull } from '@recruitee/offers-types';
import { JobSchedulerInfoComponent } from '@recruitee/offers-ui';
import {
  BadgeComponent,
  ButtonComponent,
  ClickToCopyDirective,
  DateComponent,
  DividerComponent,
  FixedSelectOptionsHelper,
  GroupComponent,
  IconColorDirective,
  IconComponent,
  PlaceloaderComponent,
  RtTooltip,
  SelectOption,
} from '@recruitee/user-interface';

import { ActionsDropdownContainerComponent } from '../../containers/actions-dropdown-container.component';
import { StatusesDropdownContainerComponent } from '../../containers/statuses-dropdown-container.component';
import { offerCardVisibleFieldsFallback } from '../../views/offer-list-item/types';
import {
  Counter,
  OffersFooterCountersComponent,
} from './offers-footer-counters/offers-footer-counters.component';

@Component({
  selector: 'rt-offers-list-item',
  templateUrl: './offers-list-item.component.html',
  styleUrls: ['./offers-list-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.as-placeholder]': '!isVisibleOnScreen',
  },
  standalone: true,
  imports: [
    BadgeComponent,
    ClickToCopyDirective,
    RtTooltip,
    PlaceloaderComponent,
    DividerComponent,
    IconColorDirective,
    IconComponent,
    DateComponent,
    GroupComponent,
    ButtonComponent,
    RtAnalyticsModule,
    ActionsDropdownContainerComponent,
    OffersFooterCountersComponent,
    StatusesDropdownContainerComponent,
    RouterLink,
    TranslatePipe,
    NgTemplateOutlet,
    AssignedLocationsInfoContainerComponent,
    FollowButtonComponent,
    JobSchedulerInfoComponent,
    TrackDirective,
  ],
})
export class OffersListItemComponent {
  @Input() public offer: OfferFull;
  @Input() public sort: string;
  @Input() public detailsRouterLink: string[];
  @Input() public isVisibleOnScreen: boolean;
  @Input() public counters: Counter[];
  @Input() public fieldsVisibilityMap = offerCardVisibleFieldsFallback;

  @Output()
  public offerToggleFollow: EventEmitter<{
    followed: boolean;
    id: number;
    slug: string;
  }> = new EventEmitter();

  public followLoading: boolean = false;
  // expose types to template
  public OfferKind: typeof OfferKind = OfferKind;

  public abilities = {
    manageJobs: false,
    viewAcquisition: false,
  };
  public departmentLabelTooltipEnabled = false;
  public locationIds: number[] = [];

  private readonly prioritiesSelectOptions: SelectOption[] =
    this.fixedSelectOptions.getPrioritySelectOptions();
  private ngOnDestroy$ = new Subject<void>();

  @ViewChild('departmentRef') public set departmentRef(departmentRef: ElementRef) {
    if (departmentRef) this.setDepartmentTooltipEnabled(departmentRef);
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store,
    private router: Router,
    private fixedSelectOptions: FixedSelectOptionsHelper,
    @Optional() private jobAnalytics: OfferAnalytics,
  ) {}

  public get hasEdit(): boolean {
    return this.abilities.manageJobs && this.offer.status !== OfferStatus.Archived;
  }

  public get priorityOption(): SelectOption | undefined {
    return this.prioritiesSelectOptions.find(option => option.value === this.offer.priority);
  }

  public get hasElementsBeforePriority() {
    return (
      (this.fieldsVisibilityMap.department && this.offer.department) ||
      (this.fieldsVisibilityMap.location && this.locationIds.length) ||
      ((this.offer.onSite || this.offer.hybrid || this.offer.remote) &&
        this.fieldsVisibilityMap.work_model)
    );
  }

  public ngOnInit(): void {
    this.checkAbilities();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.offer) {
      this.followLoading = false;
      this.locationIds = this.offer.locations?.map(location => location.id) ?? [];
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public editOffer() {
    if (this.offer.kind === OfferKind.Job && this.jobAnalytics.trackEdit) {
      const status = this.offer.status === OfferStatus.Published ? 'public' : this.offer.status;
      this.jobAnalytics.trackEdit(status);
    }
    const redirect =
      this.offer.kind === OfferKind.Job
        ? `/offers/${this.offer.slug}/edit`
        : `/talent_pools/${this.offer.slug}/edit`;

    this.router.navigate([redirect]);
  }

  public followClick(): void {
    this.followLoading = true;
    this.cdRef.detectChanges();
    this.offerToggleFollow.emit(this.offer);
  }

  private checkAbilities(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.abilities = {
          manageJobs: enabledMap[Ability.ManageJobs],
          viewAcquisition: enabledMap[Ability.ViewAcquisition],
        };
        this.cdRef.detectChanges();
      });
  }

  private setDepartmentTooltipEnabled(departmentRef: ElementRef) {
    if (this.offer.kind === OfferKind.Job) {
      this.departmentLabelTooltipEnabled =
        departmentRef.nativeElement.offsetWidth < departmentRef.nativeElement.scrollWidth;
      this.cdRef.detectChanges();
    }
  }
}
